import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";
import { ArrowRight, Check, Eye, EyeOff, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError, apiRequest } from "@/lib/api-client";
import { localizeError } from "@/lib/errorMessages";
import { useAuthStore, type AuthUser } from "@/lib/auth-store";
import { cn } from "@/lib/utils";
import type { components } from "@/types/api";

const loginSchema = z.object({
  identifier: z
    .string()
    .trim()
    .min(1, "Vui lòng nhập email hoặc số điện thoại")
    .refine(
      (value) => {
        const compact = value.replace(/\s+/g, "");
        return (
          z.string().email().safeParse(value).success ||
          /^(\+84|0)\d{9,10}$/.test(compact)
        );
      },
      { message: "Email hoặc số điện thoại không hợp lệ" },
    ),
  password: z.string().min(1, "Vui lòng nhập mật khẩu"),
});

type LoginFormValues = z.infer<typeof loginSchema>;
type LoginResponseWire = components["schemas"]["LoginResponse"];
type LoginResponse = LoginResponseWire & {
  accessToken: string;
  user: AuthUser;
};

export function LoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { identifier: "", password: "" },
  });

  const identifierValue = watch("identifier");
  const identifierValid =
    loginSchema.shape.identifier.safeParse(identifierValue).success;

  const onSubmit = async (values: LoginFormValues) => {
    setSubmitting(true);
    try {
      const response = await apiRequest<LoginResponse>("/auth/login", {
        method: "POST",
        body: {
          email: values.identifier.trim().replace(/\s+/g, ""),
          password: values.password,
        },
        skipAuth: true,
      });
      setAuth(response.accessToken, response.user);
      navigate("/properties", { replace: true });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        toast.error("Email/số điện thoại hoặc mật khẩu không đúng");
      } else if (error instanceof ApiError) {
        toast.error(localizeError(error));
      } else {
        toast.error("Không thể kết nối đến máy chủ");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell>
      <AuthHeading
        eyebrow="Đăng nhập"
        title={
          <>
            Đăng nhập <span className="text-primary">tài khoản.</span>
          </>
        }
        subtitle="Đăng nhập để tiếp tục với workspace bất động sản của bạn."
      />
      <form
        className="mt-8 flex flex-col gap-4"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <Field
          id="identifier"
          label="Email hoặc số điện thoại"
          error={errors.identifier?.message}
          leadingIcon={<Mail className="size-4 text-muted-foreground" />}
          trailingButton={
            identifierValid ? (
              <Check className="size-4 text-success" aria-hidden="true" />
            ) : undefined
          }
        >
          <Input
            id="identifier"
            type="text"
            autoComplete="username"
            inputMode="text"
            placeholder="ban@example.com hoặc 0901234567"
            className="h-12 border-0 bg-transparent pl-10 pr-9 shadow-none focus-visible:ring-0"
            {...register("identifier")}
            aria-invalid={errors.identifier ? "true" : "false"}
          />
        </Field>
        <Field
          id="password"
          label="Mật khẩu"
          rightSlot={
            <Link
              to="/forgot-password"
              className="text-[11px] font-medium text-primary hover:underline"
            >
              Quên mật khẩu?
            </Link>
          }
          error={errors.password?.message}
          trailingButton={
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
              className="text-muted-foreground"
            >
              {showPassword ? (
                <EyeOff className="size-4" />
              ) : (
                <Eye className="size-4" />
              )}
            </button>
          }
        >
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            placeholder="••••••••"
            className="h-12 border-0 bg-transparent pl-4 pr-10 shadow-none focus-visible:ring-0"
            {...register("password")}
            aria-invalid={errors.password ? "true" : "false"}
          />
        </Field>
        <AuthSubmit pending={submitting} pendingLabel="Đang đăng nhập...">
          Đăng nhập
        </AuthSubmit>
      </form>
      <p className="mt-6 text-center text-sm text-muted-foreground">
        Chưa có tài khoản?{" "}
        <span className="font-semibold text-foreground">
          Liên hệ quản trị viên
        </span>
      </p>
    </AuthShell>
  );
}

// ---------------------------------------------------------------------------
// Shared auth chrome — used by login / forgot / reset.
// Two-column split: a dark marketing aside (≥ lg) + the form column. On
// mobile the aside collapses and a compact brand mark leads the form.
// ---------------------------------------------------------------------------

function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <main className="flex min-h-svh flex-col bg-background lg:flex-row">
      <AuthAside />
      <section className="flex flex-1 flex-col justify-center px-6 py-12 sm:px-12">
        <div className="mx-auto w-full max-w-[440px]">
          {/* Mobile-only brand (the aside that carries it is hidden < lg). */}
          <div className="mb-10 lg:hidden">
            <BrandHeader />
          </div>
          {children}
        </div>
      </section>
    </main>
  );
}

function AuthAside() {
  return (
    <aside className="relative hidden overflow-hidden bg-brand-dark p-12 text-white lg:flex lg:w-[52%] lg:shrink-0 lg:flex-col lg:justify-between">
      {/* Layered backdrop: indigo gradient + two glows + faint grid. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-br from-[#221c54] via-[#17143a] to-[#0c0a22]"
      />
      <div
        aria-hidden="true"
        className="absolute -left-20 top-[38%] size-[460px] rounded-full bg-primary/25 blur-[130px]"
      />
      <div
        aria-hidden="true"
        className="absolute -right-10 -top-10 size-[360px] rounded-full bg-[#4733b3]/40 blur-[120px]"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.14] [background-image:linear-gradient(rgba(255,255,255,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.5)_1px,transparent_1px)] [background-size:42px_42px] [mask-image:radial-gradient(80%_60%_at_30%_30%,black_0%,transparent_80%)]"
      />

      {/* Brand */}
      <div className="relative z-10 flex items-center gap-3">
        <div className="flex size-11 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-brand-glow text-xl font-bold text-white shadow-[0_8px_24px_rgba(79,70,229,0.45)]">
          E
        </div>
        <div className="leading-tight">
          <div className="text-xl font-bold tracking-tight">Estate</div>
          <div className="text-[11px] text-white/45">
            realty workspace
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="relative z-10 max-w-md">
        <p className="font-mono text-[11px] tracking-[0.22em] text-white/40">
          QU.TR.HÀ.NỘI · 2026
        </p>
        <h2 className="mt-5 text-4xl font-bold leading-[1.08] tracking-tight">
          Quản lý <span className="text-[#a9b3ff]">danh mục bất động sản</span>{" "}
          trong một workspace.
        </h2>
        {/* No invented metrics here — a fake count on the front door is the
            fastest way to teach users to distrust every number inside. */}
        <p className="mt-5 text-sm leading-relaxed text-white/55">
          Kho hàng, khách hàng, pipeline và lịch hẹn — tất cả trong một nơi.
          Estate giúp bạn vận hành cả đội môi giới từ điện thoại đến desktop.
        </p>
        <div className="mt-8 grid grid-cols-3 gap-3">
          <AsideStat value="BĐS" label="Kho hàng tập trung" />
          <AsideStat value="CRM" label="Khách hàng & lead" />
          <AsideStat value="24/7" label="Trên mọi thiết bị" />
        </div>
      </div>

      {/* Footer */}
      <div className="relative z-10 flex items-center justify-between text-[11px] text-white/35">
        <span>© 2026 estate.crm</span>
        <span>v2.4.0 · sb-hn-01</span>
      </div>
    </aside>
  );
}

function AsideStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3.5">
      <div className="text-2xl font-bold tracking-tight tabular-nums">
        {value}
      </div>
      <div className="mt-0.5 text-[12px] text-white/50">{label}</div>
    </div>
  );
}

function BrandHeader() {
  return (
    <div className="flex items-center gap-3">
      <div className="flex size-11 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-foreground text-xl font-bold text-primary-foreground shadow-[0_8px_24px_rgba(79,70,229,0.3)]">
        E
      </div>
      <div className="leading-tight">
        <div className="text-xl font-bold tracking-tight">Estate</div>
        <div className="text-[11px] text-muted-foreground">
          realty workspace
        </div>
      </div>
    </div>
  );
}

function AuthHeading({
  eyebrow,
  icon,
  title,
  subtitle,
}: {
  eyebrow: string;
  icon?: React.ReactNode;
  title: React.ReactNode;
  subtitle: React.ReactNode;
}) {
  return (
    <div className="flex flex-col">
      {icon ? (
        <div className="mb-6 flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          {icon}
        </div>
      ) : null}
      <p className="font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-ink-faint">
        {eyebrow}
      </p>
      <h1 className="mt-2.5 text-[28px] font-bold tracking-tight">
        {title}
      </h1>
      <div className="mt-3 text-sm leading-relaxed text-muted-foreground">
        {subtitle}
      </div>
    </div>
  );
}

/** The shared dark, full-width primary submit button with a trailing arrow. */
function AuthSubmit({
  pending,
  pendingLabel,
  children,
}: {
  pending: boolean;
  pendingLabel: string;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="submit"
      variant="primary"
      size="lg"
      disabled={pending}
      className="mt-2 h-12 w-full justify-center gap-2 rounded-xl shadow-[0_12px_30px_-10px_rgba(79,70,229,0.55)]"
    >
      {pending ? pendingLabel : children}
      {!pending ? <ArrowRight className="size-4" /> : null}
    </Button>
  );
}

type FieldProps = {
  id: string;
  label: string;
  error?: string | undefined;
  leadingIcon?: React.ReactNode | undefined;
  trailingButton?: React.ReactNode | undefined;
  rightSlot?: React.ReactNode | undefined;
  children: React.ReactNode;
};

function Field({
  id,
  label,
  error,
  leadingIcon,
  trailingButton,
  rightSlot,
  children,
}: FieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <Label htmlFor={id} className="text-[14px] font-semibold text-ink-soft">
          {label}
        </Label>
        {rightSlot}
      </div>
      <div
        className={cn(
          "relative flex items-center rounded-xl border bg-card shadow-[var(--shadow-soft)] transition-colors focus-within:border-ring",
          error ? "border-destructive" : "border-border-strong",
        )}
      >
        {leadingIcon ? (
          <span className="pointer-events-none absolute left-3.5 inline-flex">
            {leadingIcon}
          </span>
        ) : null}
        {children}
        {trailingButton ? (
          <span className="absolute right-3.5 inline-flex">
            {trailingButton}
          </span>
        ) : null}
      </div>
      {error ? (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export { AuthShell, AuthHeading, AuthSubmit, BrandHeader, Field };
